import type { ProcessStepExecutionStatus } from '@/api/processStepsApi'
import { STATUS_META } from '@/lib/journey'
import { PROCESS_STEP_STATUS_META } from '@/lib/processStepStatus'
import type { ProcedureType } from '@/types/cgt'

/**
 * Pure rules for My Work (/my-steps): the reviews assigned to the signed-in
 * user and the process steps assigned to them. Kept out of the page so they
 * are unit-tested (vitest here runs `.ts` only).
 */

export type MyWorkTab = 'reviews' | 'steps'

/**
 * The tab to show: the one asked for when the user can see it, else the first
 * they can. The top bar's review button asks for `reviews`.
 */
export function myWorkTab(asked: string | null, canReview: boolean, canExecute: boolean): MyWorkTab {
  if (asked === 'reviews' && canReview) return 'reviews'
  if (asked === 'steps' && canExecute) return 'steps'
  return canReview ? 'reviews' : 'steps'
}

/** What a work item belongs to, labelled for what it is: a patient, a kit or a work order. */
export function workSubject(row: {
  procedureType?: ProcedureType | null
  patientMrn: string | null
  workOrderNumber?: string | null
}): { label: string; value: string } | null {
  // A kit run keeps its supply id where a patient run keeps the MRN.
  if (row.procedureType === 'KitCreation' && row.patientMrn) return { label: 'Kit', value: row.patientMrn }
  if (row.procedureType === 'MES' && row.workOrderNumber) return { label: 'Work order', value: row.workOrderNumber }
  if (row.patientMrn) return { label: 'Patient', value: row.patientMrn }
  if (row.workOrderNumber) return { label: 'Work order', value: row.workOrderNumber }
  return null
}

/**
 * A status chip: its filter value, its words and its colours. `chip` carries
 * background and text, the same shape as the app's other status maps.
 */
export interface WorkStatus {
  value: string
  label: string
  chip: string
  dot: string
}

export type ReviewFilter = 'all' | 'ready' | 'waiting'

export const REVIEW_FILTERS: { value: ReviewFilter; label: string }[] = [
  { value: 'all', label: 'All reviews' },
  { value: 'ready', label: 'Ready to sign' },
  { value: 'waiting', label: 'Waiting on the form' },
]

/**
 * Where a review stands. Ready to sign once its form is completed, in the
 * yellow the procedure tables use for "Pending review"; until then, where the
 * form itself is.
 */
export function reviewStatus(r: { ready: boolean; submissionStatus?: string | null }): WorkStatus {
  if (r.ready) {
    return { value: 'ready', label: 'Ready to sign', chip: STATUS_META.pending_review.badge, dot: STATUS_META.pending_review.dot }
  }
  if (r.submissionStatus === 'draft') {
    return { value: 'waiting', label: 'Form not started', chip: STATUS_META.not_started.badge, dot: STATUS_META.not_started.dot }
  }
  return { value: 'waiting', label: 'Form in progress', chip: STATUS_META.in_progress.badge, dot: STATUS_META.in_progress.dot }
}

/** An assigned step's status, in the words and colours its form shows. */
export function stepStatus(status: ProcessStepExecutionStatus): WorkStatus {
  const meta = PROCESS_STEP_STATUS_META[status] ?? PROCESS_STEP_STATUS_META.ASSIGNED
  return { value: status, label: meta.label, chip: meta.chip, dot: meta.dot }
}

/** Reviews ready to sign first, each group in the order it came. */
export function readyFirst<T extends { ready: boolean }>(items: readonly T[]): T[] {
  return [...items.filter(i => i.ready), ...items.filter(i => !i.ready)]
}

/** Whether any of a row's shown values contains `q`, case-insensitively. An empty `q` matches. */
export function matchesSearch(q: string, values: readonly (string | null | undefined)[]): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return values.some(v => v?.toLowerCase().includes(needle))
}

/** `Sep 23, 2026, 10:58 AM`, or null. */
export function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** "2 reviews", or "1 of 2 reviews" while a search or filter hides some. */
export function countLabel(shown: number, total: number, noun: string): string {
  const word = total === 1 ? noun : `${noun}s`
  return shown === total ? `${total} ${word}` : `${shown} of ${total} ${word}`
}
